import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { PaymentProofFields, submitPaymentProof } from '../components/DepositTransfer';
import { useLocation, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, Calendar as CalIcon, Truck, MessageCircle, Phone } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as ShadCalendar } from '../components/ui/calendar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOT_GROUPS = [
  ['Early morning', ['06:00','06:30','07:00','07:30']],
  ['Morning', ['08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30']],
  ['Afternoon', ['12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30','16:00','16:30']],
  ['Evening', ['17:00','17:30','18:00','18:30','19:00','19:30','20:00','20:30','21:00','21:30','22:00','22:30','23:00']],
];

const toIsoDate = (d) => {
  if (!d) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/** Add N hours to a "HH:MM" time string, wrapping past midnight. */
const addHours = (hhmm, hours) => {
  if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return '';
  const [hStr, mStr] = hhmm.split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10) || 0;
  if (Number.isNaN(h)) return '';
  const total = (h + hours) * 60 + m;
  const outH = Math.floor((total % (24 * 60) + 24 * 60) % (24 * 60) / 60);
  const outM = total % 60;
  return `${String(outH).padStart(2, '0')}:${String(outM).padStart(2, '0')}`;
};

const useQuoteIdFromUrlOrStorage = () => {
  const search = useLocation().search;
  return useMemo(() => {
    const q = new URLSearchParams(search).get('quote');
    if (q) return q;
    try {
      const raw = localStorage.getItem('mv_pending_quote');
      if (raw) {
        const p = JSON.parse(raw);
        if (p?.id) return p.id;
      }
    } catch (_) { /* ignore */ }
    return null;
  }, [search]);
};

const PaidProofForm = ({ quoteId, deposit, onDone }) => {
  const [reference, setReference] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await submitPaymentProof(quoteId, reference, file); onDone(); }
    catch (e2) { toast.error(e2?.response?.data?.detail || 'Something went wrong. Please WhatsApp us.'); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-[60vh] bg-[color:var(--mv-sky)] py-10 px-4">
      <form onSubmit={submit} className="max-w-xl mx-auto bg-white rounded-2xl border border-slate-200 p-6 space-y-4" data-testid="paid-proof-form">
        <h1 className="font-display text-2xl font-extrabold text-[color:var(--mv-navy)]">Tell us how you paid your £{deposit} deposit</h1>
        <PaymentProofFields quoteId={quoteId} reference={reference} setReference={setReference} file={file} setFile={setFile} />
        <button type="submit" disabled={busy || reference.trim().length < 3} className="w-full h-11 rounded-md bg-[color:var(--mv-navy)] text-white text-sm font-bold disabled:opacity-60" data-testid="paid-proof-submit">
          {busy ? 'Saving…' : 'I’ve paid the deposit — continue'}
        </button>
      </form>
    </div>
  );
};

export default function Paid() {
  const quoteId = useQuoteIdFromUrlOrStorage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quote, setQuote] = useState(null);
  const [claimed, setClaimed] = useState(false);

  // Booking state (unlocks after claim)
  const [bookDate, setBookDate] = useState(null);
  const [bookTime, setBookTime] = useState('');
  const [bookChecked, setBookChecked] = useState(false);
  const [bookChecking, setBookChecking] = useState(false);
  const [bookConfirming, setBookConfirming] = useState(false);
  const [booking, setBooking] = useState(null);

  const sameDaySurcharge = quote?.same_day_surcharge ?? 35;
  const basePrice = quote?.price ?? 0;
  const sameDay = bookDate && toIsoDate(bookDate) === toIsoDate(new Date());
  const finalPrice = basePrice + (sameDay ? sameDaySurcharge : 0);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!quoteId) {
        setError('missing');
        setLoading(false);
        return;
      }
      try {
        // Fetch the quote
        const { data } = await axios.get(`${API}/estimate/${quoteId}`);
        if (cancelled) return;
        setQuote(data);
        if (data.booking) setBooking(data.booking);
        setClaimed(!!data.deposit_claimed_at);
        // Clear the pending-quote crumb now that we've landed
        try { localStorage.removeItem('mv_pending_quote'); } catch (_) {}
      } catch (e) {
        if (!cancelled) setError('notfound');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [quoteId]);

  const runAvailabilityCheck = () => {
    if (!bookDate || !bookTime || bookChecking) return;
    setBookChecking(true);
    setTimeout(() => { setBookChecking(false); setBookChecked(true); }, 900);
  };

  const confirmBooking = async () => {
    if (!bookDate || !bookTime || bookConfirming) return;
    setBookConfirming(true);
    try {
      const { data } = await axios.post(`${API}/estimate/${quoteId}/book`, {
        date: toIsoDate(bookDate),
        time: bookTime,
        same_day: !!sameDay,
      });
      setBooking(data.booking);
      toast.success(sameDay
        ? "Same-day booking locked in — now WhatsApp us so we don't miss it."
        : `Booking confirmed for ${toIsoDate(bookDate)} at ${bookTime}.`);
    } catch (e) {
      toast.error("Couldn't lock in the booking. Please WhatsApp us and we'll sort it.");
    } finally {
      setBookConfirming(false);
    }
  };

  const sameDayWhatsAppUrl = useMemo(() => {
    const txt = encodeURIComponent(
      `URGENT — SAME-DAY booking just made on the website.\n\n` +
      `Name: ${quote?.name || ''}\n` +
      `Route: ${quote?.from_postcode || ''} → ${quote?.to_postcode || ''}\n` +
      `Date/Time: ${bookDate ? toIsoDate(bookDate) : 'today'} ${bookTime || ''}\n` +
      `Final total: £${finalPrice} (incl. £${sameDaySurcharge} same-day surcharge).\n\n` +
      `Please confirm van + crew availability now.`
    );
    return `https://wa.me/447985291097?text=${txt}`;
  }, [quote, bookDate, bookTime, finalPrice, sameDaySurcharge]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 className="w-5 h-5 animate-spin text-[color:var(--mv-orange)]" />
          Loading your booking…
        </div>
      </div>
    );
  }

  if (!error && quote && !claimed && !booking) return <PaidProofForm quoteId={quoteId} deposit={quote.deposit} onDone={() => setClaimed(true)} />;

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center bg-white rounded-xl border border-slate-200 shadow-sm p-8">
          <div className="w-14 h-14 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mx-auto">
            <CalIcon className="w-6 h-6" />
          </div>
          <h1 className="font-display text-2xl font-extrabold text-[color:var(--mv-navy)] mt-4">
            We couldn't find your quote
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            If you've just paid, please WhatsApp us with your quote reference and we'll check it for you.
          </p>
          <div className="mt-5 flex flex-col gap-2">
            <a href="https://wa.me/447985291097" target="_blank" rel="noreferrer" className="bg-[#25D366] hover:bg-[#1ebc5a] text-white font-bold py-2.5 rounded-md inline-flex items-center justify-center gap-2">
              <MessageCircle className="w-4 h-4" /> Message us on WhatsApp
            </a>
            <Link to="/" className="text-sm text-[color:var(--mv-navy)] font-semibold hover:text-[color:var(--mv-orange)]">
              ← Back to Move Van Ltd
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] bg-[color:var(--mv-sky)] py-10 px-4">
      <div className="max-w-xl mx-auto">
        <div className="rounded-2xl bg-white shadow-sm border border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-br from-[color:var(--mv-navy)] to-[#13315c] text-white p-6">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/40 text-emerald-100 px-3 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase">
              <CheckCircle2 className="w-3.5 h-3.5" /> Payment details received
            </div>
            <div className="font-display text-3xl font-extrabold mt-3">Thanks{quote?.name ? `, ${quote.name}` : ''}!</div>
            <div className="text-sm text-white/80 mt-1">
              We've notified the Move Van Ltd team. Quote <strong>#{(quoteId || '').slice(0,6).toUpperCase()}</strong> · £{quote?.price}
              {quote?.van_recommendation && <> · {quote.van_recommendation}</>}.
            </div>
          </div>

          {/* Booking widget */}
          <div className="p-6">
            {booking ? (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4" data-testid="paid-booking-confirmed">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="font-bold text-[color:var(--mv-navy)]">Booking confirmed · {booking.date} · {booking.time}</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      Final total: <strong className="text-[color:var(--mv-navy)]">£{booking.final_price}</strong>
                      {booking.same_day && <> (incl. <strong>+£{sameDaySurcharge} same-day surcharge</strong>)</>}
                    </div>
                  </div>
                </div>
                {booking.same_day && (
                  <div className="mt-3 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-900">
                    <div className="font-bold">WhatsApp us <u>right now</u> so we don't miss your same-day job.</div>
                    <a href={sameDayWhatsAppUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1ebc5a] text-white text-sm font-bold px-4 py-2 rounded-md">
                      <MessageCircle className="w-4 h-4" /> WhatsApp Move Van Ltd
                    </a>
                  </div>
                )}
                <div className="mt-4 text-xs text-slate-500">
                  A confirmation email will land shortly. If anything changes, call <a href="tel:07985291097" className="font-bold text-[color:var(--mv-navy)]">07985 291097</a>.
                </div>
              </div>
            ) : (
              <>
                <div className="text-xs font-bold tracking-[0.22em] uppercase text-[color:var(--mv-orange)]">
                  Final step · Pick your date & time
                </div>
                <h2 className="font-display text-xl font-extrabold text-[color:var(--mv-navy)] mt-1">Lock in your slot</h2>
                <p className="text-sm text-slate-600 mt-1">Thanks, we'll check your payment against your reference. Now choose when the van turns up.</p>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" data-testid="paid-book-date-btn" className="h-11 w-full rounded-md border border-slate-200 bg-white text-left pl-9 pr-3 text-sm text-[color:var(--mv-navy)] relative hover:border-slate-300">
                        <CalIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        {bookDate ? format(bookDate, 'PPP') : <span className="text-slate-400">Choose booking date</span>}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <ShadCalendar
                        mode="single"
                        selected={bookDate}
                        onSelect={(d) => { setBookDate(d); setBookChecked(false); }}
                        disabled={{ before: new Date(new Date().setHours(0,0,0,0)) }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <div className="relative">
                    <CalIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <select
                      data-testid="paid-book-time-select"
                      value={bookTime}
                      onChange={(e) => { setBookTime(e.target.value); setBookChecked(false); }}
                      className="h-11 w-full appearance-none rounded-md border border-slate-200 bg-white pl-9 pr-9 text-sm text-[color:var(--mv-navy)] focus:outline-none focus:ring-2 focus:ring-[color:var(--mv-orange)]"
                    >
                      <option value="">Pick a time slot…</option>
                      {TIME_SLOT_GROUPS.map(([label, items]) => (
                        <optgroup key={label} label={label}>
                          {items.map(t => <option key={t} value={t}>{t}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                </div>

                {sameDay && (
                  <div className="mt-3 rounded-lg bg-red-50 border-2 border-red-200 p-3 text-sm text-red-900">
                    <div className="font-black uppercase tracking-wider text-xs">🚨 Same-day booking · +£{sameDaySurcharge}</div>
                    <div className="mt-1">Same-day jobs carry a <strong>£{sameDaySurcharge} surcharge</strong> and you <strong>must WhatsApp us immediately after booking</strong> so we don't miss anything urgent.</div>
                  </div>
                )}

                {!bookChecked ? (
                  <button
                    type="button"
                    onClick={runAvailabilityCheck}
                    disabled={!bookDate || !bookTime || bookChecking}
                    data-testid="paid-book-check-btn"
                    className="mt-3 w-full h-11 rounded-md bg-[color:var(--mv-navy)] hover:bg-[#13315c] disabled:opacity-50 text-white text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
                  >
                    {bookChecking ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking live calendar…</> : <><CalIcon className="w-4 h-4" /> Check availability</>}
                  </button>
                ) : (
                  <>
                    <div className="mt-3 rounded-lg bg-green-50 border border-green-200 p-3" data-testid="paid-book-available-banner">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <div className="font-bold text-[color:var(--mv-navy)]">We have a booking slot available!</div>
                          <div className="text-slate-600 text-xs mt-0.5">
                            {format(bookDate, 'EEE, d MMM yyyy')} · <span className="font-semibold">{bookTime}</span> — provisionally held for 10 minutes.
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-green-200 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                        <div className="flex items-start gap-2">
                          <Truck className="w-4 h-4 text-[color:var(--mv-orange)] shrink-0 mt-0.5" />
                          <div>
                            <div className="font-bold text-[color:var(--mv-navy)]">Van & crew ready</div>
                            <div className="text-slate-600">{quote?.van_recommendation || 'A van & crew team'} · {quote?.hours_estimate || 'confirmed'}</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                          <div>
                            <div className="font-bold text-[color:var(--mv-navy)]">Final total</div>
                            <div className="text-slate-600">£{finalPrice}{sameDay && <> (incl. £{sameDaySurcharge} same-day)</>}</div>
                          </div>
                        </div>
                      </div>
                      {sameDay && (
                        <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900">
                          <strong>Same-day arrival window:</strong> we have <strong>availability between {bookTime} and {addHours(bookTime, 6)}</strong>. We'll WhatsApp you a tighter ETA the moment the crew is assigned.
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={confirmBooking}
                      disabled={bookConfirming}
                      data-testid="paid-book-confirm-btn"
                      className="mt-3 w-full h-11 rounded-md bg-[color:var(--mv-orange)] hover:bg-orange-600 disabled:opacity-60 text-white text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
                    >
                      {bookConfirming ? <><Loader2 className="w-4 h-4 animate-spin" /> Locking in your slot…</> : <><CheckCircle2 className="w-4 h-4" /> Confirm booking · £{finalPrice}</>}
                    </button>
                  </>
                )}
              </>
            )}

            <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
              <Link to="/" className="font-semibold hover:text-[color:var(--mv-orange)]">← Back to home</Link>
              <a href="tel:07985291097" className="inline-flex items-center gap-1 font-semibold hover:text-[color:var(--mv-orange)]">
                <Phone className="w-3.5 h-3.5" /> 07985 291097
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
